import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import { LucideLayers, Map, Satellite, Sun, Moon, X } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';

const baseLayers = [
  { key: 'White', label: 'White', icon: <Sun size={20} /> },
  { key: 'Dark', label: 'Dark', icon: <Moon size={20} /> },
  { key: 'Satellite', label: 'Satellite', icon: <Satellite size={20} /> },
  { key: 'Streets', label: 'Streets', icon: <Map size={20} /> },
];
// Define the color map at the top of your component
const labelToColor = {
    "Slab Depth": "#450061",
    "Heatmap": "#ff4500",
    "Tectonic Plate": "#ff5500",
    "Timor-Leste": "black",
    "Area of Interest": "#FFC107",
    "200Km Buffer": "#ff8c00",
    "IOC Sea Level": "green",
    "Unit Sources": "#0000ff",
    "Slab Dip": "#00ffd9",
    "Slab Strike": "#80ff00",
    // Add more mappings here
  };
  
const BaseMapLayerControl = ({ layers = [], toggleLayer, basemap, setBasemap }) => {
  const [open, setOpen] = useState(false);
  const paneRef = useRef();
  const panelRef = useCallback((node) => {
    paneRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const btnRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (paneRef.current && !paneRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="position-absolute top-0 end-0 m-3" style={{ zIndex: 1000 }}>
      {/* Toggle Button */}
      <div ref={btnRef} style={{ position: 'relative', zIndex: 1000 }}>
        <button
          onClick={() => setOpen(!open)}
          title={open ? 'Close Map Layers Panel' : 'Map Layers'}
          aria-label="Toggle Layer Panel"
          style={{
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: 18,
            color: '#000',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1.0)')}
        >
          {open ? <X /> : <LucideLayers />}
        </button>
      </div>

      {/* Panel */}
      {open && (
        <div
          ref={panelRef}
          className="bg-white border rounded shadow p-3 mt-2"
          style={{
            width: 240,
            position: 'absolute',
            // bottom: 25, // not required
            right: 0,
            zIndex: 1000,
          }}
        >
          {/* Base Maps */}
          <h6 className="text-secondary fw-bold mb-2">Base Maps</h6>
          <div className="d-flex flex-wrap gap-2 mb-2">
            {baseLayers.map((b) => (
              <button
                key={b.key}
                title={b.label}
                aria-label={b.label}
                onClick={() => setBasemap(b.key)}
                className={`btn d-flex align-items-center justify-content-center p-2 rounded border ${
                  basemap === b.key ? 'btn-primary text-white' : 'btn-light border-secondary'
                }`}
                style={{ width: 36, height: 36, transition: 'transform 0.2s' }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                {b.icon}
              </button>
            ))}
          </div>

          {/* Overlay Layers */}
          {layers.length > 0 && (
            <>
              <hr className="my-3" />
              <h6 className="text-secondary fw-bold mb-2">Overlay Layers</h6>
              {layers.map(({ label, visible, color }) => (
                <div key={label} className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center">
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: labelToColor[label] || '#999',
                        marginRight: 8,
                      }}
                    />
                    <label className="form-check-label" htmlFor={label} style={{ fontSize: '0.9rem' }}>
                      {label}
                    </label>
                  </div>
                  <div className="form-check form-switch m-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id={label}
                      checked={visible}
                      onChange={() => toggleLayer(label)}
                      style={{
                        backgroundColor: visible ? '#007bff' : '',
                        borderColor: visible ? '#007bff' : '',
                        cursor: 'pointer',
                      }}
                    />
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BaseMapLayerControl;
